import { NavLink } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ChartBar,
  CalendarBlank,
  CalendarCheck,
  Users,
  Wrench,
  Palette,
  Browser,
  Sparkle,
  Lock,
  ShieldStar
} from 'phosphor-react';
import { useAuth } from '../context/AuthContext';
import Logo from '../assets/Flat Logo White.png';
import { cn } from '../utils/cn';
import { hasPermission } from '../utils/permissions';

const EASE = [0.16, 1, 0.3, 1];

const groupVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.045, delayChildren: 0.08 } }
};

const itemVariants = {
  hidden: { opacity: 0, x: -12 },
  show: { opacity: 1, x: 0, transition: { duration: 0.4, ease: EASE } }
};

export default function Sidebar() {
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();
  const hasPerm = (perm) => hasPermission(user, perm);

  const username = user?.username || 'Operator';
  const initials = username
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'SK';
  const roleLabel = user?.role_title || (user?.is_superadmin ? 'Super Admin' : 'Staff');

  const workspaceItems = [
    { to: '/', icon: ChartBar, label: 'Dashboard', end: true },
    { to: '/sessions', icon: CalendarBlank, label: 'Session Calendar', perm: 'sessions' },
    { to: '/bookings', icon: CalendarCheck, label: 'Booking Management', perm: 'bookings' },
    { to: '/customers', icon: Users, label: 'Customer Management', perm: 'users' },
    { to: '/services', icon: Wrench, label: 'Service Management', perm: 'services' },
    { to: '/portfolio', icon: Palette, label: 'Portfolio Management', perm: 'portfolio' }
  ].filter((item) => !item.perm || hasPerm(item.perm));

  const homepageItems = [
    { to: '/homepage-editor', icon: Browser, label: 'Homepage Editor', perm: 'homepage' },
    { to: '/studio-assistant', icon: Sparkle, label: 'AI Assistant', perm: 'studio-assistant' },
    { to: '/privacy-terms', icon: Lock, label: 'Privacy & Terms', perm: 'privacy-terms' }
  ].filter((item) => hasPerm(item.perm));

  return (
    <aside
      aria-label="Sidebar navigation"
      className="fixed flex h-[100dvh] w-[280px] flex-col overflow-hidden border-r border-white/[0.06] bg-[#081214] text-slate-200"
    >
      {/* Ambient mesh + grain */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_100%_0%,rgba(20,184,166,0.16),transparent_55%),radial-gradient(90%_65%_at_0%_110%,rgba(16,185,129,0.10),transparent_55%)]" />
      <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-teal-400/10 blur-[100px]" />
      <div className="ambient-noise" />

      {/* Logo section */}
      <div className="relative flex items-center px-6 pt-6 pb-5">
        <div className="relative">
          <div className="pointer-events-none absolute -inset-3 bg-teal-400/15 blur-2xl" />
          <img
            src={Logo}
            alt="Studio Kosmos Logo"
            className="relative block h-[50px] w-auto object-contain drop-shadow-[0_6px_18px_rgba(20,184,166,0.35)]"
          />
        </div>
      </div>

      <div className="relative mx-6 h-px bg-gradient-to-r from-transparent via-teal-300/20 to-transparent" />

      {/* Navigation */}
      <nav
        aria-label="Primary"
        className="relative mt-4 flex flex-1 flex-col gap-7 overflow-y-auto px-4 pb-4 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
      >
        <motion.div
          variants={groupVariants}
          initial={reduceMotion ? false : 'hidden'}
          animate="show"
          className="flex flex-col gap-1"
        >
          <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-teal-100/35">
            Workspace
          </div>
          {workspaceItems.map((item) => (
            <motion.li key={item.to} variants={itemVariants} className="list-none">
              <SidebarNavLink {...item} />
            </motion.li>
          ))}
        </motion.div>

        {homepageItems.length > 0 && (
          <motion.div
            variants={groupVariants}
            initial={reduceMotion ? false : 'hidden'}
            animate="show"
            className="flex flex-col gap-1"
          >
            <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-teal-100/35">
              Homepage
            </div>
            {homepageItems.map((item) => (
              <motion.li key={item.to} variants={itemVariants} className="list-none">
                <SidebarNavLink {...item} />
              </motion.li>
            ))}
          </motion.div>
        )}

        {/* Admin section */}
        <motion.div
          variants={groupVariants}
          initial={reduceMotion ? false : 'hidden'}
          animate="show"
          className="mt-auto flex flex-col gap-1 pt-2"
        >
          <div className="mx-3 mb-2 h-px bg-gradient-to-r from-transparent via-teal-300/15 to-transparent" />
          <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-teal-100/35">
            System
          </div>
          <motion.li variants={itemVariants} className="list-none">
            <SidebarNavLink to="/admin" icon={ShieldStar} label="Staff Management" />
          </motion.li>
        </motion.div>
      </nav>

      {/* User card */}
      <div className="relative border-t border-white/[0.06] p-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
          <div className="flex items-center gap-3 rounded-[14px] bg-gradient-to-b from-white/[0.06] to-transparent px-3 py-2.5">
            <div className="relative">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(20,184,166,0.7)] ring-1 ring-white/20">
                {initials}
              </div>
              <div className="absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#081214] bg-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold leading-tight text-white">{username}</p>
              <p className="mt-0.5 truncate text-[11px] font-medium text-teal-100/50">{roleLabel}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#081214] to-transparent" />
    </aside>
  );
}

function SidebarNavLink({ to, icon: Icon, label, end }) {
  const base = cn(
    'group relative flex items-center gap-3 rounded-xl px-3 py-[9px] text-[0.845rem] font-medium tracking-[0.01em] text-teal-100/55 transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] outline-none',
    'hover:bg-white/[0.05] hover:text-white',
    'focus-visible:ring-2 focus-visible:ring-teal-300/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#081214]',
    'before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-[3px] before:rounded-r-full before:bg-gradient-to-b before:from-teal-300 before:to-emerald-400 before:opacity-0',
    'before:shadow-[0_0_12px_2px_rgba(45,212,191,0.45)] before:transition-all before:duration-200 before:ease-[cubic-bezier(0.16,1,0.3,1)]'
  );

  const active = cn(
    'bg-gradient-to-r from-teal-500/[0.16] via-teal-500/[0.07] to-transparent text-white',
    'ring-1 ring-teal-300/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]',
    'before:opacity-100'
  );

  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => cn(base, isActive && active)}
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              'relative z-10 flex h-7 w-7 items-center justify-center rounded-lg text-teal-100/60 transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]',
              'group-hover:scale-110 group-hover:text-teal-200',
              isActive && 'text-teal-300'
            )}
          >
            <Icon size={18} weight="light" />
          </span>
          <span className="relative z-10 flex-1 truncate">{label}</span>

          <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-r from-teal-300/[0.06] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        </>
      )}
    </NavLink>
  );
}